import { type FC, type ReactNode, useMemo } from 'react'
import { Checkbox } from '@/components/commons/checkbox'
import { ImageXIcon } from '@/components/ui/icons'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import {
	type BadgeType,
	ImageBadge
} from '@/features/photoset/components/image-badge'
import type { Image } from '@/features/photoset/types.ts'
import { cn } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'

interface ImageCardProps {
	asSkeleton?: undefined
	onSelect?: ((isShiftPressed: boolean) => void) | null
	isSelected: boolean
	image: Omit<Image, 'space'>
	thumbnail?: string | null
	className?: string
	isLoading?: boolean
	loadingText?: string
	endSlot?: ReactNode
	imageNameIcon?: ReactNode
	hideImageInfo?: boolean
	hideCheckbox?: boolean
	showBadge?: boolean
	badgeType?: BadgeType
}

const ImageCardCheckboxSlot = ({
	hideCheckbox,
	isLoading,
	loadingText,
	isSelected,
	onSelect
}: {
	hideCheckbox: boolean
	isLoading?: boolean
	loadingText?: string
	isSelected: boolean
	onSelect?: ((isShiftPressed: boolean) => void) | null
}) => {
	if (hideCheckbox) {
		return null
	}
	if (isLoading) {
		return loadingText ? (
			<div className='flex items-center gap-2 whitespace-nowrap font-medium text-xs'>
				<Spinner className='fill-neutral-400' size={20} />
				<span>{loadingText}</span>
			</div>
		) : (
			<Spinner className='fill-neutral-400' size={20} />
		)
	}
	return (
		<Checkbox
			checked={isSelected}
			disabled={isLoading}
			onChange={(event) =>
				'shiftKey' in event.nativeEvent
					? onSelect?.(Boolean(event.nativeEvent.shiftKey))
					: onSelect?.(false)
			}
		/>
	)
}

interface ImageCardSkeletonProps {
	asSkeleton: true
	onSelect?: undefined
	isSelected?: undefined
	image?: undefined
	thumbnail?: undefined
	isLoading?: false
	loadingText?: undefined
	className?: string
	endSlot?: ReactNode
	imageNameIcon?: ReactNode
	hideImageInfo?: boolean
	hideCheckbox?: boolean
	badgeType?: undefined
	showBadge?: boolean
}

export const ImageCard: FC<ImageCardProps | ImageCardSkeletonProps> = ({
	className,
	onSelect,
	isSelected,
	image,
	thumbnail,
	asSkeleton,
	isLoading,
	loadingText,
	endSlot,
	imageNameIcon,
	hideImageInfo = false,
	hideCheckbox = false,
	badgeType,
	showBadge
}) => {
	const { t } = useTranslation()
	const isMergedHdr = useMemo(() => image?.is_hdr, [image])

	if (asSkeleton) {
		return (
			<div
				className={cn(
					'relative flex flex-col items-stretch gap-[18px] p-6 focus-within:bg-neutral-100 focus-within:shadow-[inset_0_0_0_1px_var(--color-neutral-200)] [&:not(:hover)_.show-on-hover]:hidden',
					className
				)}
			>
				<Skeleton style={{ aspectRatio: '3/2' }} />
				<div className='flex items-center justify-between gap-2'>
					<Skeleton height={20} width={80} />
					<Skeleton height={20} width={20} />
				</div>
			</div>
		)
	}

	return (
		<div
			className={cn(
				'relative flex flex-col items-stretch gap-[18px] p-6 focus-within:bg-neutral-100 focus-within:shadow-[inset_0_0_0_1px_var(--color-neutral-200)] [&:not(:hover)_.show-on-hover]:hidden',
				className
			)}
		>
			{isMergedHdr && (
				<div className='absolute top-9 rounded-r-xl bg-blue-500 px-1.5 py-1 font-medium text-[10px] text-neutral-white'>
					{t('common:merged_hdr')}
				</div>
			)}
			{thumbnail ? (
				<button
					className={cn(
						'block aspect-3/2 w-full overflow-hidden rounded-xl bg-neutral-white shadow-sm transition-opacity duration-250 ease-in-out',
						onSelect ? 'cursor-pointer' : 'cursor-default',
						isSelected &&
							'outline-4 outline-primary-500 outline-offset-4',
						isLoading && 'opacity-60'
					)}
					draggable={false}
					onClick={(event) =>
						!isLoading && onSelect?.(event.shiftKey)
					}
					type='button'
				>
					{/* biome-ignore lint/correctness/useImageSize: the image is from backend and the size is not known */}
					<img
						alt={image.name}
						className='size-full rounded-xl object-contain'
						loading='lazy'
						src={thumbnail}
					/>
				</button>
			) : (
				<button
					className={cn(
						'flex aspect-[3/2] w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-xl bg-neutral-white font-medium text-sm shadow-sm',
						isSelected &&
							'outline outline-4 outline-primary-500 outline-offset-4'
					)}
					onClick={(event) =>
						!isLoading && onSelect?.(event.shiftKey)
					}
					type='button'
				>
					<ImageXIcon size={18} />
					<p>{t('errors:unavailable', { subject: 'preview' })}</p>
				</button>
			)}

			<div className='flex flex-col gap-4'>
				{!hideImageInfo && (
					<section className='flex w-full items-center justify-between gap-2'>
						<div className='flex gap-2 overflow-hidden'>
							{imageNameIcon}
							<h4 className='w-full max-w-full flex-grow overflow-hidden text-ellipsis whitespace-nowrap font-medium text-neutral-900 text-sm'>
								{image.name}
							</h4>
						</div>
						<ImageCardCheckboxSlot
							hideCheckbox={hideCheckbox}
							isLoading={isLoading}
							isSelected={isSelected}
							loadingText={loadingText}
							onSelect={onSelect}
						/>

						{((hideCheckbox && isSelected) || showBadge) &&
							badgeType && <ImageBadge type={badgeType} />}
					</section>
				)}
				{endSlot}
			</div>
		</div>
	)
}

ImageCard.displayName = 'ImageCard'
